import merge from 'lodash.merge';
import {Theme} from '@material-ui/core/styles';
import {Overrides} from '@material-ui/core/styles/overrides';
import {PaletteOptions} from '@material-ui/core/styles/createPalette';
import {breakpoints} from '../../components/constants';
import {createEpicTheme as createEpicThemeBase} from '@epic-ui/theme';
import {darkColors} from './dark.override';
import {MuiPickersOverrides} from '@material-ui/pickers/typings/overrides';

import {
    calendar,
    createPickersOverrides
} from '../../components/common/date-range-picker/overrides';

export type EpicThemeType = 'dark' | 'light';

type EpicOverrides = Overrides &
    MuiPickersOverrides & {
        MuiPickersStaticWrapper: object;
        MuiPickersYearSelection: object;
        MuiPickersBasePicker: object;
    };

// SaC specific styles
const defaultCustomOverrides = ({overrides: defaultOverrides, ...theme}: Theme): EpicOverrides => {
    const overrides: EpicOverrides = {
        MuiPickersBasePicker: {
            pickerView: {
                flex: 1,
                minHeight: 250
            }
        },
        MuiPickersToolbar: {
            toolbar: {
                flex: 1,
                backgroundColor: theme.palette.background.paper
            }
        },
        MuiPickersStaticWrapper: {
            staticWrapperRoot: {
                width: '100%',
                minWidth: '100%',
                // @ts-ignore
                backgroundColor: theme.palette.background.overlays
            }
        },
        MuiPickersYearSelection: {
            container: {
                height: 240,
                marginTop: theme.spacing(2)
            }
        },
        MuiIconButton: {
            root: {
                borderRadius: '50%'
            }
        },
        MuiTooltip: {
            popper: {
                pointerEvents: 'all'
            }
        },
        MuiAlert: {
            root: {
                alignItems: 'normal',
                [theme.breakpoints.down('xs')]: {
                    flexDirection: 'column'
                }
            },
            standardInfo: {
                backgroundColor: theme.palette.background.paper
            },
            message: {
                width: '100%'
            },
            action: {
                flex: '20%',
                justifyContent: 'flex-end',
                minWidth: 'unset',
                minHeight: 'unset',
                padding: theme.spacing(0, 2),
                '& > a ~ a, & > a ~ button, & > button ~ button': {
                    marginLeft: theme.spacing(2)
                },
                [theme.breakpoints.down('xs')]: {
                    marginLeft: 0,
                    padding: 0
                }
            },
            icon: {
                [theme.breakpoints.down('xs')]: {
                    display: 'none'
                }
            }
        },
        MuiTypography: {
            h1: {
                fontSize: theme.typography.pxToRem(30),
                '&> span': {
                    fontSize: theme.typography.pxToRem(30)
                },
                [theme.breakpoints.up('flg')]: {
                    fontSize: theme.typography.pxToRem(40),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(40)
                    }
                },

                [theme.breakpoints.up('lg')]: {
                    fontSize: theme.typography.pxToRem(50),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(50)
                    }
                }
            },
            h2: {
                fontSize: theme.typography.pxToRem(26),
                '&> span': {
                    fontSize: theme.typography.pxToRem(26)
                },
                [theme.breakpoints.up('fmd')]: {
                    fontSize: theme.typography.pxToRem(26),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(26)
                    }
                },
                [theme.breakpoints.up('flg')]: {
                    fontSize: theme.typography.pxToRem(30),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(30)
                    }
                },
                [theme.breakpoints.up('lg')]: {
                    fontSize: theme.typography.pxToRem(36),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(36)
                    }
                }
            },
            h3: {
                fontSize: theme.typography.pxToRem(20),
                '&> span': {
                    fontSize: theme.typography.pxToRem(20)
                },
                [theme.breakpoints.up('flg')]: {
                    fontSize: theme.typography.pxToRem(22),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(22)
                    }
                },

                [theme.breakpoints.up('lg')]: {
                    fontSize: theme.typography.pxToRem(30),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(30)
                    }
                }
            },
            h4: {
                fontWeight: 500,
                fontSize: theme.typography.pxToRem(16),
                '&> span': {
                    fontWeight: 500,
                    fontSize: theme.typography.pxToRem(16)
                },
                [theme.breakpoints.up('flg')]: {
                    fontSize: theme.typography.pxToRem(18),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(18)
                    }
                },

                [theme.breakpoints.up('lg')]: {
                    fontSize: theme.typography.pxToRem(20),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(20)
                    }
                }
            },
            h5: {
                fontSize: theme.typography.pxToRem(16),
                '&> span': {
                    fontSize: theme.typography.pxToRem(16)
                },
                [theme.breakpoints.up('lg')]: {
                    fontSize: theme.typography.pxToRem(18),
                    '&> span': {
                        fontSize: theme.typography.pxToRem(18)
                    }
                }
            },
            body1: {
                [theme.breakpoints.up('xl')]: {
                    fontSize: theme.typography.pxToRem(16)
                }
            },
            body2: {
                [theme.breakpoints.up('xl')]: {
                    fontSize: theme.typography.pxToRem(14)
                }
            },
            caption: {
                [theme.breakpoints.up('xl')]: {
                    fontSize: theme.typography.pxToRem(14)
                }
            }
        },
        ...createPickersOverrides(theme)
    };

    return merge(defaultOverrides, overrides);
};

export const createEpicTheme = (type: EpicThemeType = 'dark') => {
    let overrides: Overrides;
    let palette: PaletteOptions;

    if (type === 'dark') {
        palette = darkColors;
    }

    const theme = createEpicThemeBase({
        palette: {
            ...palette,
            calendar
        },
        breakpoints
    });

    theme.overrides = merge(defaultCustomOverrides(theme), overrides);

    return theme;
};

export default createEpicTheme;
